//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import moment from 'moment-timezone';

import {
  TWITTER_API_DATE_FORMAT,
  ITwitterAudienceDailyStats,
  ITwitterDailyEngagementMetric,
} from '@apis/twitter-analytics/twitterAnalyticsApiModel';
import {
  DateRangeAlias,
  getAliasedDateRange,
  IAliasedDateRange,
} from '@components/date-range-picker/dateRangePickerModels';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { ITwitterDailyAudienceMetric } from '@twitterV2/redux/audience/twitterAudienceModel';
import { getDatesInRange } from '@utils/getDatesInRange';
import { normalizeToMap } from '@utils/normalize';

import { crl8 } from 'app/curalate.globals';

export function getCustomDateRangePickerAliases(
  timeZone: TimeZoneId,
  aliases: DateRangeAlias[] = [
    'last7Days',
    'last30Days',
    'last3Months',
    'last1Year',
  ],
): IAliasedDateRange[] {
  return aliases.map((alias) => getAliasedDateRange(alias, timeZone));
}

const getFormattedDate = (d: Date | string) => moment(d).format('YYYY-MM-DD');

export function fillInMissingEngagementDates(
  days: ITwitterDailyEngagementMetric[],
  start: DateString,
  end: DateString,
): ITwitterDailyEngagementMetric[] {
  const { byId: existingDaysMap } = normalizeToMap(days, (day) =>
    getFormattedDate(DateString.fromString(day.date, TWITTER_API_DATE_FORMAT)),
  );

  const timezone = crl8.session?.timeZone.id ?? TimeZoneId.Utc;
  const startDate = DateString.toDate(start, timezone);
  const endDate = DateString.toDate(end, timezone);

  return getDatesInRange(startDate, endDate, { inclusive: true }).map(
    (date) => {
      const dateKey = getFormattedDate(date);
      const existingDay = existingDaysMap.get(dateKey);
      return (
        existingDay ?? {
          date: dateKey,
          metrics: {
            impressions: 0,
            engagements: 0,
            linkClicks: 0,
            avgEngagementRate: 0,
          },
        }
      );
    },
  );
}

export function fillInMissingAudienceDates(
  days: Record<string, ITwitterAudienceDailyStats>,
  start: DateString,
  end: DateString,
): ITwitterDailyAudienceMetric[] {
  const timezone = crl8.session?.timeZone.id ?? TimeZoneId.Utc;
  const startDate = DateString.toDate(start, timezone);
  const endDate = DateString.toDate(end, timezone);

  const dailyStatsByKey = Object.keys(days);
  const dailyStats = dailyStatsByKey.map((key) => {
    return {
      date: DateString.format(
        DateString.fromTimestamp(parseInt(key), timezone),
        'YYYY-MM-DD',
      ),
      metrics: {
        followers: days[key]?.followers,
        changeInFollowers: days[key]?.changeInFollowers,
      },
    };
  });
  const { byId: existingDaysMap } = normalizeToMap(dailyStats, (day) =>
    getFormattedDate(DateString.fromString(day.date, 'YYYY-MM-DD')),
  );

  return getDatesInRange(startDate, endDate, { inclusive: false }).map(
    (date) => {
      const dateKey = getFormattedDate(date);
      const existingDay = existingDaysMap.get(dateKey);
      return (
        existingDay ?? {
          date: dateKey,
          metrics: {
            followers: 0,
            changeInFollowers: 0,
          },
        }
      );
    },
  );
}
